import React, { useState, useMemo } from 'react';
import { clsx } from 'clsx';
import {
  PlusIcon,
  ArrowsPointingOutIcon,
  ArrowsPointingInIcon,
  RectangleGroupIcon,
} from '@heroicons/react/24/outline';
import { PlanRectangle } from '../../services/planService';
import { Button, EmptyState } from '../ui';
import RectangleNode from './RectangleNode';

interface PlanCanvasProps {
  rectangles: PlanRectangle[];
  selectedRectangleId: number | null;
  onSelectRectangle: (rectangle: PlanRectangle | null) => void;
  onAddRectangle: (parentId?: number) => void;
  onEditRectangle: (rectangle: PlanRectangle) => void;
  onDeleteRectangle: (rectangle: PlanRectangle) => void;
  loading?: boolean;
}

const PlanCanvas: React.FC<PlanCanvasProps> = ({
  rectangles,
  selectedRectangleId,
  onSelectRectangle,
  onAddRectangle,
  onEditRectangle,
  onDeleteRectangle,
  loading,
}) => {
  const [expandedIds, setExpandedIds] = useState<Set<number>>(new Set());
  const [isAllExpanded, setIsAllExpanded] = useState(true);

  // Get root rectangles (no parent)
  const rootRectangles = useMemo(() => {
    return rectangles.filter((r) => r.parentId === null);
  }, [rectangles]);

  // Get children for a rectangle
  const getChildren = (parentId: number) => {
    return rectangles.filter((r) => r.parentId === parentId);
  };

  const handleToggleExpand = (id: number) => {
    setExpandedIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  const handleExpandAll = () => {
    if (isAllExpanded) {
      setExpandedIds(new Set());
    } else {
      setExpandedIds(new Set(rectangles.map((r) => r.id)));
    }
    setIsAllExpanded(!isAllExpanded);
  };

  // Initialize all expanded on first load
  React.useEffect(() => {
    if (rectangles.length > 0 && expandedIds.size === 0) {
      setExpandedIds(new Set(rectangles.map((r) => r.id)));
    }
  }, [rectangles]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary-200 border-t-primary-600" />
      </div>
    );
  }

  if (rectangles.length === 0) {
    return (
      <div className="flex items-center justify-center h-96">
        <EmptyState
          icon={<RectangleGroupIcon className="h-12 w-12" />}
          title="No nodes in this plan"
          description="Start building your plan by adding the first node"
          action={
            <Button
              onClick={() => onAddRectangle()}
              leftIcon={<PlusIcon className="h-5 w-5" />}
            >
              Add Root Node
            </Button>
          }
        />
      </div>
    );
  }

  return (
    <div className="relative">
      {/* Toolbar */}
      <div className="sticky top-0 z-10 bg-white/90 backdrop-blur-sm border-b border-gray-200 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-500">
            {rectangles.length} node{rectangles.length !== 1 ? 's' : ''}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleExpandAll}
            leftIcon={
              isAllExpanded ? (
                <ArrowsPointingInIcon className="h-4 w-4" />
              ) : (
                <ArrowsPointingOutIcon className="h-4 w-4" />
              )
            }
          >
            {isAllExpanded ? 'Collapse All' : 'Expand All'}
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={() => onAddRectangle()}
            leftIcon={<PlusIcon className="h-4 w-4" />}
          >
            Add Root
          </Button>
        </div>
      </div>

      {/* Tree View */}
      <div className="p-4 space-y-3">
        {rootRectangles.map((rectangle) => (
          <RectangleNode
            key={rectangle.id}
            rectangle={rectangle}
            children={getChildren(rectangle.id)}
            allRectangles={rectangles}
            selectedId={selectedRectangleId}
            expandedIds={expandedIds}
            onSelect={onSelectRectangle}
            onToggleExpand={handleToggleExpand}
            onAddChild={(parent) => onAddRectangle(parent.id)}
            onEdit={onEditRectangle}
            onDelete={onDeleteRectangle}
          />
        ))}
      </div>
    </div>
  );
};

export default PlanCanvas;

